// @flow

import React, { useState } from "react";
import { localize } from "redux-i18n";
import { route } from "@routes/helper";

import { PermissionEnum } from "@modules/account/components";
import { USER_TYPES } from "@modules/account/constants";
import { View, Text } from "@components";

import "./sidebar.style.scss";
import type { ProfileUser } from "@modules/account/types";
import { arrayRemoveItem } from "@utils/object";

type Props = {
  clientId: ?number,
  employeeId: ?number,
  user: ProfileUser,
  currentPath: string,
  permissions: { [appPermission: string]: boolean },
  clientSettings: { [appPermission: string]: boolean },
  navigate: (url: string) => void,
  isCollapsed: boolean,
  ...PropsFromLocalize,
};

type StateProps = {
  selectedNode: NavNode,
};

type NavNode = {
  id: string,
  name: string,
  url: string | null,
  icon: string,
  appPermission: string | string[] | boolean,
  clientPermission?: string,
  children?: Array<NavNode>,
  isDisabled: boolean,
  isHidden?: boolean,
  color?: string,
  handleNavClick?: Function, // in case we want to provide custom behaviour, like for Empployees
  separator?: "top" | "right" | "bottom" | "left",
};

function Sidebar(props: Props) {
  const currentURL = props.currentPath;
  const isCollapsed = props.isCollapsed;
  const navTreeArray = generateNavTreeArray();
  const nodeByUrl = findNavNodeByProp(navTreeArray, "url", currentURL, false);

  const [state, setState] = useState({
    selectedNode: nodeByUrl || navTreeArray[0],
  });
  const [isExpanded, setIsExpanded] = useState([]);

  function handleNavClick(nodeId: string, navigateNow?: boolean) {
    return function() {
      const node = findNavNodeByProp(navTreeArray, "id", nodeId);
      if (node && node.url) props.navigate(node.url);

      let res = [...isExpanded];
      if (res.includes(nodeId)) res = arrayRemoveItem(isExpanded, nodeId);
      else res = [...isExpanded, nodeId];

      setIsExpanded([...res]);
    };
  }

  function findNavNodeByProp(
    nodes: Array<Object>,
    prop: string,
    value: string,
    allowNotExactMatch: boolean = false,
  ) {
    for (const node of nodes) {
      const patternString = `${node[prop]}(?=\/[0-9a-z-]+)`;
      const regex = new RegExp(patternString, "g");
      const notExactMatch = (allowNotExactMatch && !!value.match(regex)) || false;
      if ((node[prop] && node[prop] === value) || notExactMatch) {
        return node;
      }
      if (node.children) {
        const childNode = findNavNodeByProp(node.children, prop, value, allowNotExactMatch);
        if (childNode) {
          return childNode;
        }
      }
    }
    return null;
  }

  function hasPermission(appPermission: string | string[] | boolean, clientPermission?: string) {
    if (typeof appPermission === "boolean") return appPermission;
    else {
      const { permissions, clientSettings } = props;
      let hasClientAccess;

      if (clientPermission && clientSettings) {
        hasClientAccess = !!clientSettings[clientPermission];
      } else hasClientAccess = !clientPermission;
      const hasPermission = !!Object.keys(permissions).filter((perm: string) => {
        if (typeof appPermission === "string") {
          return perm.indexOf(appPermission) > -1;
        } else {
          return typeof appPermission === "object" && appPermission.includes(perm);
        }
      }).length;

      return hasClientAccess && hasPermission;
    }
  }

  function renderMenuItem(el: NavNode, isChild?: boolean = false) {
    const children = el.children;
    const childWithSameUrl = children && children.find((child: NavNode) => child.url === el.url);

    const isActive = !childWithSameUrl && state.selectedNode?.id === el.id;
    const isDisabled = el.isDisabled;

    const url = el.url;

    const parentOfActiveChild =
      !isActive &&
      children &&
      children.find((child: NavNode) => {
        return state.selectedNode?.id === child.id;
      });

    const hasActiveChild =
      (isActive || parentOfActiveChild || isExpanded.includes(el.id)) && "expanded";
    return (
      <View
        name={el.id}
        className={["item-wrap", isActive && "active", isDisabled && "disabled"]}
        key={el.id}
        separator={el.hasOwnProperty("separator") && el.separator}
      >
        <View
          direction="row"
          className="item"
          overflow="hidden"
          justifyContent="space-between"
          style={{ border: "1px solid red" }}
          flex={1}
          onClick={
            !isActive && el.handleNavClick ? el.handleNavClick : handleNavClick(el.id, !!el.url)
          }
          onMouseOver={(e: React.MouseEvent) => {
            if (isCollapsed) {
              const label = e.target.querySelector(".label");
              if (label) {
                const rect = e.target.getBoundingClientRect();
                label.classList.add("act-as-tooltip");
                label.style.top = `${rect.top + 5}px`;
              }
            }
          }}
          onMouseOut={(e: React.MouseEvent) => {
            if (isCollapsed) {
              const label = e.target.querySelector(".label");
              label && label.classList.remove("act-as-tooltip");
              label.style.top = "";
            }
          }}
        >
          <View
            id={el.icon}
            color={el.color}
            className={[
              "no-pointer-events",
              "material-icons",
              isDisabled && "disabled",
              isChild && "child",
            ]}
          >
            {el.icon}
          </View>

          <Text id={el.name} className="label flex-1 no-pointer-events" text={el.name} />
          {children && (
            <View
              className={[
                "no-pointer-events",
                "material-icons",
                "right-arrow",
                hasActiveChild && "transform-rotate-90",
              ]}
            >
              keyboard_arrow_right
            </View>
          )}
        </View>
        {children && hasActiveChild && (
          <View className={["children-wrapper", hasActiveChild && "expanded"]}>
            {children && hasActiveChild && (
              <View>{children.map(child => renderMenuItem(child, true))}</View>
            )}
          </View>
        )}
      </View>
    );
  }

  function filterNavNodes(navTree: NavNode[]) {
    return navTree.filter((el: NavNode) => {
      if (el.children) el.children = filterNavNodes(el.children);
      return (
        el.isHidden !== true &&
        el.appPermission &&
        hasPermission(el.appPermission, el.clientPermission)
      );
    });
  }

  function renderNavigation() {
    const filteredItems = filterNavNodes(navTreeArray);

    const itemsToRender = filteredItems.map((el: NavNode, idx: number) => {
      return renderMenuItem(el);
    });
    return (
      <View name="render-navigation" flex={1} className="items-container">
        {itemsToRender}
      </View>
    );
  }

  function generateNavTreeArray(): Array<NavNode> {
    const clientId = props?.clientId;
    const { employeeId, clientSettings, user } = props;
    const userType = user.userType;
    const healthProvidersAllowedUserTypes =
      [USER_TYPES.ADMIN, USER_TYPES.CLIENT_ADMIN, USER_TYPES.SERVICE_MANAGER].indexOf(
        user.userType,
      ) > -1;

    const showHealthReports =
      healthProvidersAllowedUserTypes && clientSettings?.serviceType === "COMPLETE_SUPPORT";
    const showProvidersReports =
      healthProvidersAllowedUserTypes && clientSettings?.eapSignposting === true;
    const hideMyGoodShape = user.isNonService === true;

    return [
      //home
      {
        id: "home",
        name: `${props.t("HOME")} `,
        url: route("HOME_PATH"),
        icon: "home",
        appPermission: true,
        isDisabled: false,
        separator: "bottom",
        isHidden: ![
          USER_TYPES.ABSENCE_ADVISOR,
          USER_TYPES.NURSE_ADVISOR,
          USER_TYPES.LEAD_NURSE_ADVISOR,
          USER_TYPES.CONTACT_CENTRE_MANAGER,
          USER_TYPES.SERVICE_MANAGER,
          USER_TYPES.ADMIN,
        ].includes(userType),
      },
      {
        id: "callbacks",
        name: props.t("CALLBACKS"),
        url: route("RECORDING_OUTBOUND_CALLS_PATH"),
        icon: "support_agent",
        appPermission: true,
        isDisabled: false,
        separator: "bottom",
        isHidden: ![
          USER_TYPES.NURSE_ADVISOR,
          USER_TYPES.LEAD_NURSE_ADVISOR,
          USER_TYPES.CONTACT_CENTRE_MANAGER,
          USER_TYPES.ADMIN,
        ].includes(userType),
      },
      {
        id: "dashboard",
        name: props.t("DASHBOARD"),
        url: route("MANAGEMENT_CONTACT_CENTRE_DASHBOARD_PATH"),
        icon: "speed",
        appPermission: true,
        isDisabled: false,
        isHidden: ![USER_TYPES.CONTACT_CENTRE_MANAGER, USER_TYPES.ADMIN].includes(userType),
      },
      {
        id: "queue_admin",
        name: props.t("QUEUE_ADMIN"),
        url: route("MANAGEMENT_CONTACT_CENTRE_QUEUE_MANAGEMENT_PATH"),
        icon: "date_range",
        appPermission: true,
        isDisabled: false,
        isHidden: ![USER_TYPES.CONTACT_CENTRE_MANAGER, USER_TYPES.ADMIN].includes(userType),
      },
      {
        id: "script_admin",
        name: props.t("SCRIPT_ADMIN"),
        url: route("MANAGEMENT_CONTACT_CENTRE_SCRIPT_MANAGEMENT_PATH"),
        icon: "sms",
        appPermission: true,
        isDisabled: false,
        isHidden: ![USER_TYPES.CONTACT_CENTRE_MANAGER, USER_TYPES.ADMIN].includes(userType),
      },
      {
        id: "servicedesk_admin",
        name: props.t("SERVICEDESK_ADMIN"),
        url: route("MANAGEMENT_CONTACT_CENTRE_SERVICE_DESK_MANAGEMENT_PATH"),
        icon: "confirmation_number",
        appPermission: true,
        isDisabled: false,
        isHidden: ![USER_TYPES.SERVICE_MANAGER, USER_TYPES.ADMIN].includes(userType),
      },
      {
        id: "notices_admin",
        name: props.t("NOTICES_ADMIN"),
        url: route("MANAGEMENT_SYSTEM_MESSAGES_PATH"),
        icon: "campaign",
        appPermission: true,
        isDisabled: false,
        isHidden: ![USER_TYPES.CONTACT_CENTRE_MANAGER, USER_TYPES.ADMIN].includes(userType),
      },
      {
        id: "health_notices",
        name: props.t("HEALTH_NOTICES"),
        url: route("HEALTH_NOTICES_MANAGEMENT_PATH"),
        icon: "campaign",
        appPermission: PermissionEnum.PRIVILEGE_HEALTH_NOTICES_WRITE,
        isDisabled: false,
        separator: "bottom",
      },
      {
        id: "incident_register",
        name: props.t("INCIDENT_REGISTER"),
        url: route("MANAGEMENT_CLINICAL_GOVERNANCE_INCIDENT_REGISTER_PATH"),
        icon: "wb_twilight",
        appPermission: true,
        isDisabled: false,
        isHidden: ![
          USER_TYPES.NURSE_ADVISOR,
          USER_TYPES.LEAD_NURSE_ADVISOR,
          USER_TYPES.CONTACT_CENTRE_MANAGER,
          USER_TYPES.ADMIN,
        ].includes(userType),
      },
      {
        id: "incident_rates",
        name: props.t("INCIDENT_RATES"),
        url: route("MANAGEMENT_CLINICAL_GOVERNANCE_INCIDENT_RATES_REPORT_PATH"),
        icon: "timeline",
        appPermission: true,
        isDisabled: false,
        separator: "bottom",
        isHidden: ![
          USER_TYPES.NURSE_ADVISOR,
          USER_TYPES.LEAD_NURSE_ADVISOR,
          USER_TYPES.CONTACT_CENTRE_MANAGER,
          USER_TYPES.ADMIN,
        ].includes(userType),
      },
      {
        id: "image_library",
        name: props.t("IMAGE_LIBRARY"),
        url: route("MANAGEMENT_IMAGE_LIBRARY_PATH"),
        icon: "image",
        appPermission: true,
        isDisabled: false,
        isHidden: ![USER_TYPES.CONTACT_CENTRE_MANAGER, USER_TYPES.ADMIN].includes(userType),
      },
      {
        id: "classifications",
        name: props.t("CLASSIFICATIONS"),
        url: route("MANAGEMENT_CLASSIFICATIONS_PATH"),
        icon: "category",
        isDisabled: false,
        appPermission: PermissionEnum.PRIVILEGE_HEALTH_CLASSIFICATIONS,
      },
      {
        id: "interventions",
        name: props.t("INTERVENTIONS"),
        url: route("MANAGEMENT_INTERVENTIONS_PATH"),
        icon: "healing",
        appPermission: true,
        isDisabled: false,
        appPermission: PermissionEnum.PRIVILEGE_HEALTH_CLASSIFICATIONS,
      },
    ];
  }

  const navigation = renderNavigation();
  return (
    <View flex={1} position="sticky" className={["sidebar", props.isCollapsed && "collapsed"]}>
      <View
        name="logo-container"
        direction="row"
        className="logo"
        justifyContent="center"
        alignItems="center"
        gap={isCollapsed ? 0 : 2}
        height={`67px`}
        separator="bottom"
      >
        <a href={route("HOME_PATH")} className="picto"></a>
        <Text className="text" text={props.t("HEALTH")} />
      </View>
      {navigation}
    </View>
  );
}

export default localize()(Sidebar);
